import { collection, query, where, getDocs, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';

// Check if user has reached max referral coupons (5)
export const checkReferralCouponLimit = async (userId) => {
  try {
    const couponsRef = collection(db, "coupons");
    const q = query(
      couponsRef, 
      where("referrerId", "==", userId),
      where("isReferralCoupon", "==", true),
      where("isActive", "==", true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.length < 5; // Max 5 referral coupons
  } catch (error) {
    console.error("Error checking referral coupon limit:", error);
    return false;
  }
};

// Check if user has used welcome coupon before
export const checkWelcomeCouponUsage = async (userId) => {
  try {
    const couponsRef = collection(db, "coupons");
    const q = query(
      couponsRef, 
      where("referredUserId", "==", userId),
      where("isWelcomeCoupon", "==", true)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty; // If any welcome coupon exists, user has used one
  } catch (error) {
    console.error("Error checking welcome coupon usage:", error);
    return false;
  }
};

// Create welcome coupon for referred user
export const createWelcomeCoupon = async (referredUserId, referredUserEmail) => {
  try {
    const couponData = {
      code: `WELCOME100-${referredUserId.slice(-6)}`,
      discountPercent: 100,
      maxDiscountAmount: 100,
      expiryDate: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 10 days
      usageLimit: 1,
      description: "Welcome offer - ₹100 off first subscription",
      usedCount: 0,
      isActive: true,
      usedBy: {},
      createdAt: serverTimestamp(),
      isWelcomeCoupon: true,
      referredUserId: referredUserId,
      referredUserEmail: referredUserEmail,
      couponType: "welcome"
    };

    const couponRef = await addDoc(collection(db, "coupons"), couponData);
    console.log("✅ Welcome coupon created for:", referredUserEmail);
    return couponRef.id;
  } catch (error) {
    console.error("❌ Error creating welcome coupon:", error);
    return null;
  }
};

// Create referral coupon for referrer when referred user subscribes
export const createReferralCoupon = async (referrerId, referredUserId, referredUserEmail) => {
  try {
    // Check if referrer has reached max coupons
    const canCreateCoupon = await checkReferralCouponLimit(referrerId);
    if (!canCreateCoupon) {
      console.log("⚠️ Referrer has reached max 5 referral coupons");
      return null;
    }

    const couponData = {
      code: `REF150-${referrerId.slice(-6)}`,
      discountPercent: 100,
      maxDiscountAmount: 150,
      expiryDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 60 days
      usageLimit: 1,
      description: "Referral reward - ₹150 off",
      usedCount: 0,
      isActive: true,
      usedBy: {},
      createdAt: serverTimestamp(),
      isReferralCoupon: true,
      referrerId: referrerId,
      referredUserId: referredUserId,
      referredUserEmail: referredUserEmail,
      couponType: "referral"
    };

    const couponRef = await addDoc(collection(db, "coupons"), couponData);
    console.log("✅ Referral coupon created for referrer:", referrerId);
    return couponRef.id;
  } catch (error) {
    console.error("❌ Error creating referral coupon:", error);
    return null;
  }
};

// Get all coupons for a user (referral + welcome)
export const getUserCoupons = async (userId) => {
  try {
    const couponsRef = collection(db, "coupons");
    
    // Get referral coupons (where user is referrer)
    const referralQuery = query(
      couponsRef, 
      where("referrerId", "==", userId),
      where("isReferralCoupon", "==", true)
    );
    const referralSnapshot = await getDocs(referralQuery);
    const referralCoupons = referralSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      type: "referral"
    }));

    // Get welcome coupons (where user is referred)
    const welcomeQuery = query(
      couponsRef, 
      where("referredUserId", "==", userId),
      where("isWelcomeCoupon", "==", true)
    );
    const welcomeSnapshot = await getDocs(welcomeQuery);
    const welcomeCoupons = welcomeSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      type: "welcome"
    }));

    return {
      referralCoupons,
      welcomeCoupons,
      allCoupons: [...referralCoupons, ...welcomeCoupons]
    };
  } catch (error) {
    console.error("❌ Error fetching user coupons:", error);
    return { referralCoupons: [], welcomeCoupons: [], allCoupons: [] };
  }
};

// Mark referral as completed when referred user subscribes
export const markReferralCompleted = async (referralId, referrerCouponId, welcomeCouponId) => {
  try {
    await updateDoc(doc(db, "referrals", referralId), {
      status: "completed",
      referrerCouponCreated: true,
      welcomeCouponCreated: true,
      referrerCouponId: referrerCouponId,
      welcomeCouponId: welcomeCouponId,
      completedAt: serverTimestamp()
    });
    console.log("✅ Referral marked as completed");
    return true;
  } catch (error) {
    console.error("❌ Error marking referral completed:", error);
    return false;
  }
}; 